/**
 * Supabase Authentication
 * Same Supabase project as Pulse
 */

const SUPABASE_URL = 'https://kiaduadqqdixithivccx.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImtpYWR1YWRxcWRpeGl0aGl2Y2N4Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3Njk4Njg0NjUsImV4cCI6MjA4NTQ0NDQ2NX0.VPRtAFMoB_vjkXLaGi63I_KucpHtD_3eomIjDtG_Z3M';

let session = null;

/**
 * Initialize auth — check for existing session
 */
export async function initAuth() {
  const stored = localStorage.getItem('chorus_session');
  if (stored) {
    try {
      session = JSON.parse(stored);
      // Verify token is still valid
      const valid = await verifySession(session.access_token);
      if (!valid) {
        // Try refresh
        const refreshed = await refreshToken(session.refresh_token);
        if (refreshed) {
          session = refreshed;
          localStorage.setItem('chorus_session', JSON.stringify(session));
        } else {
          session = null;
          localStorage.removeItem('chorus_session');
        }
      }
    } catch {
      session = null;
      localStorage.removeItem('chorus_session');
    }
  }
  return session;
}

/**
 * Sign in with email/password
 */
export async function signIn(email, password) {
  const response = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'apikey': SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.error_description || err.msg || 'Login failed');
  }

  const data = await response.json();
  
  // Check if user has admin role
  const profile = await fetchProfile(data.access_token, data.user.id);
  if (profile?.role !== 'admin') {
    throw new Error('Access denied — admin role required');
  }

  session = {
    access_token: data.access_token,
    refresh_token: data.refresh_token,
    user: data.user,
    expires_at: Date.now() + (data.expires_in * 1000),
  };
  localStorage.setItem('chorus_session', JSON.stringify(session));
  return session;
}

/**
 * Sign out
 */
export function signOut() {
  session = null;
  localStorage.removeItem('chorus_session');
}

/**
 * Get current session
 */
export function getSession() {
  return session;
}

/**
 * Get access token for API calls
 */
export function getAccessToken() {
  return session?.access_token || null;
}

/**
 * Verify session is still valid
 */
async function verifySession(token) {
  try {
    const response = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'apikey': SUPABASE_ANON_KEY,
      },
    });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Refresh access token
 */
async function refreshToken(refreshToken) {
  try {
    const response = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!response.ok) return null;
    const data = await response.json();
    return {
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      user: data.user,
      expires_at: Date.now() + (data.expires_in * 1000),
    };
  } catch {
    return null;
  }
}

/**
 * Fetch user profile from Supabase
 */
async function fetchProfile(token, userId) {
  try {
    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/profiles?id=eq.${userId}&select=role`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'apikey': SUPABASE_ANON_KEY,
        },
      }
    );
    if (!response.ok) return null;
    const [profile] = await response.json();
    return profile;
  } catch {
    return null;
  }
}
